import { Mail } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { useEffect, useState } from 'react';

import { useFormWithValidation } from '@splitfiesta/core';
import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import type { RegisterDto } from '@splitfiesta/shared';
import {
  Alert,
  Card,
  FormButton,
  FormCheckbox,
  FormField,
  H1,
  Text,
} from '@splitfiesta/ui';

import { LegalText } from '@/components/auth/LegalText';
import { SocialLoginButtons } from '@/components/auth/SocialLoginButtons';
import { PRESIGNUP_TERMS_KEY } from '@/components/auth/TermsAcceptanceGate';
import { AuthPageSkeleton } from '@/components/common/AuthPageSkeleton';
import { LanguageSwitch } from '@/components/common/LanguageSwitch';
import {
  PasswordStrengthIndicator,
  validatePassword,
} from '@/components/common/PasswordStrengthIndicator';
import { RouteChangeLink } from '@/components/common/RouteChangeLink';
import { StoreButtons } from '@/components/common/StoreButtons';
import { Footer } from '@/components/layout/Footer';
import { ThemeToggle } from '@/components/ui/ThemeToggle';

import { useAuth } from '@/hooks/useAuth';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useRouter } from '@/hooks/useRouteChangeRouter';

import { useAuthStore } from '@/stores/authStore';

export function RegisterPage() {
  const [formData, setFormData] = useState<RegisterDto & { locale?: string }>({
    name: '',
    email: '',
    password: '',
  });
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isRegisterSuccessful, setIsRegisterSuccessful] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Stamp the acceptance for TermsAcceptanceGate: signup continues through
  // Cognito (email verify / OAuth redirect) before a backend user exists,
  // so the gate records it silently right after the first session instead
  // of re-asking. Cleared by the gate once recorded.
  useEffect(() => {
    if (termsAccepted) {
      sessionStorage.setItem(PRESIGNUP_TERMS_KEY, 'true');
    } else {
      sessionStorage.removeItem(PRESIGNUP_TERMS_KEY);
    }
  }, [termsAccepted]);

  const user = useAuthStore(state => state.user);
  const { register, isRegistering, registerError } = useAuth();
  const router = useRouter();
  const [searchParams] = useSearchParams();
  const { t, i18n, ready } = useTranslation();
  const { handleError } = useErrorHandler('auth');

  // Load required namespaces
  const authLoaded = useNamespace('auth');
  const commonLoaded = useNamespace('common');
  const errorsLoaded = useNamespace('errors');

  // Get the invitation token and language from URL params
  const inviteToken = searchParams.get('invite');
  const langParam = searchParams.get('lang');

  useEffect(() => {
    if (user) {
      // If there's an invitation token, redirect back to the invitation page
      if (inviteToken) {
        router.push(`/invite/${inviteToken}`);
      } else {
        router.push('/groups');
      }
    }
  }, [user, router, inviteToken]);

  // Form validation and submission
  const {
    handleSubmit: handleFormSubmit,
    isSubmitting,
    error,
    setError,
  } = useFormWithValidation({
    onSubmit: async () => {
      // Include current locale for localized verification emails
      register({ ...formData, locale: i18n.language });
      // Mark register as successful to keep loading state
      setIsRegisterSuccessful(true);
      // Keep loading state during redirect
      // The redirect is handled by the register function in useAuth hook
    },
    onError: (err: unknown) => {
      setError(handleError(err as Error));
      setIsRegisterSuccessful(false);
    },
    validateFn: () => {
      if (!termsAccepted) {
        return t('auth:errors.termsRequired');
      }
      if (!formData.name.trim()) {
        return t('auth:errors.nameRequired');
      }
      if (!formData.email.trim()) {
        return t('auth:errors.emailRequired');
      }
      if (formData.password !== confirmPassword) {
        return t('auth:errors.passwordMismatch');
      }
      // Use the comprehensive password validation
      const { isValid, errors } = validatePassword(formData.password);
      if (!isValid) {
        return t(errors[0]); // First unmet requirement (errors are i18n keys)
      }
      return null;
    },
  });

  // Handle register errors from the mutation
  useEffect(() => {
    if (registerError) {
      // Use setTimeout to avoid setting state during render
      const timer = setTimeout(() => {
        setError(handleError(registerError));
        setIsRegisterSuccessful(false);
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [registerError, handleError, setError]);

  // Combined loading state: either submitting or successfully registered or React Query is pending
  const isLoading = isSubmitting || isRegisterSuccessful || isRegistering;

  // Show skeleton loader while translations are loading
  if (!ready || !authLoaded || !commonLoaded || !errorsLoaded) {
    return <AuthPageSkeleton fieldCount={4} />;
  }

  return (
    <div className="min-h-screen bg-linear-to-br from-orange-400 to-red-400 dark:bg-slate-900 dark:bg-none flex flex-col">
      <div className="flex-1 p-4 pt-4">
        <div className="max-w-md w-full mx-auto">
          {/* Theme Toggle and Language Switcher - Close to top */}
          <div className="flex justify-between items-center gap-3 mb-3">
            <ThemeToggle variant="auth" data-testid="auth-theme-toggle" />
            <LanguageSwitch variant="auth" />
          </div>

          {/* Logo - Second row */}
          <div className="text-center mb-8">
            <RouteChangeLink href="/" className="inline-block">
              <div className="relative inline-block">
                <H1 className="text-white hover:text-white/90 transition-colors cursor-pointer drop-shadow-lg mb-2">
                  {t('common:app.name')}
                </H1>
              </div>
            </RouteChangeLink>
            <Text className="text-white/90">{t('common:app.tagline')}</Text>
          </div>

          <StoreButtons className="mb-6 justify-center" />

          {/* Register Form */}
          <Card>
            <div className="text-center mb-6">
              <Text className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('auth:register.title')}
              </Text>
              <Text
                variant="bodySmall"
                className="mt-1 text-gray-500 dark:text-gray-400"
              >
                {t('auth:register.subtitle')}
              </Text>
            </div>

            {error && (
              <Alert
                type="error"
                message={String(error)}
                className="mb-6"
                data-testid="register-error-message"
              />
            )}

            {/* Terms + age acceptance — gates BOTH signup paths (social and
                email), since either one creates an account. */}
            <FormCheckbox
              id="terms-accepted"
              label={<LegalText text={t('auth:legal.termsAgreement')} />}
              checked={termsAccepted}
              onChange={setTermsAccepted}
              disabled={isLoading}
              className="mb-4"
              labelClassName="font-normal"
              data-testid="register-terms-checkbox"
            />

            {/* Social Login Buttons */}
            <SocialLoginButtons
              variant="register"
              disabled={isLoading || !termsAccepted}
              oauthState={{
                invite: inviteToken || undefined,
                lang: langParam || undefined,
              }}
            />

            {/* Email Registration - Collapsible */}
            {!showEmailForm ? (
              <FormButton
                type="button"
                variant="outline"
                onClick={() => setShowEmailForm(true)}
                disabled={isLoading}
                fullWidth
                icon={Mail}
                className="mt-3 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
                data-testid="register-email-button"
              >
                {t('auth:register.signUpWithEmail')}
              </FormButton>
            ) : (
              <>
                {/* Email Form */}
                <form
                  onSubmit={e => handleFormSubmit(e, formData)}
                  className="mt-6 space-y-4"
                  data-testid="register-form"
                >
                  <FormField
                    id="name"
                    type="text"
                    label={t('common:forms.name')}
                    value={formData.name}
                    onChange={value =>
                      setFormData(prev => ({ ...prev, name: value }))
                    }
                    placeholder={t('auth:register.namePlaceholder')}
                    required
                    autoFocus
                    disabled={isLoading}
                    autoComplete="off"
                    data-testid="register-name-input"
                  />

                  <FormField
                    id="email"
                    type="email"
                    label={t('common:forms.email')}
                    value={formData.email}
                    onChange={value =>
                      setFormData(prev => ({ ...prev, email: value }))
                    }
                    placeholder={t('auth:register.emailPlaceholder')}
                    required
                    disabled={isLoading}
                    autoComplete="off"
                    data-testid="register-email-input"
                  />

                  <div className="space-y-2">
                    <FormField
                      id="password"
                      type="password"
                      label={t('common:forms.password')}
                      value={formData.password}
                      onChange={value =>
                        setFormData(prev => ({ ...prev, password: value }))
                      }
                      placeholder={t('auth:register.passwordPlaceholder')}
                      required
                      disabled={isLoading}
                      autoComplete="off"
                      data-testid="register-password-input"
                      showPasswordLabel={t('common:forms.showPassword')}
                      hidePasswordLabel={t('common:forms.hidePassword')}
                    />
                    <PasswordStrengthIndicator
                      password={formData.password}
                      className="mt-2"
                      data-testid="register-password-strength"
                    />
                  </div>

                  <div className="space-y-2">
                    <FormField
                      id="confirmPassword"
                      type="password"
                      label={t('auth:register.confirmPassword')}
                      value={confirmPassword}
                      onChange={setConfirmPassword}
                      placeholder={t(
                        'auth:register.confirmPasswordPlaceholder'
                      )}
                      required
                      disabled={isLoading}
                      autoComplete="off"
                      data-testid="register-confirm-password-input"
                      showPasswordLabel={t('common:forms.showPassword')}
                      hidePasswordLabel={t('common:forms.hidePassword')}
                    />
                    {confirmPassword && (
                      <PasswordStrengthIndicator
                        password={formData.password}
                        confirmPassword={confirmPassword}
                        showConfirmMatch={true}
                        compact={true}
                        className="mt-2"
                        data-testid="register-password-match-indicator"
                      />
                    )}
                  </div>

                  <FormButton
                    type="submit"
                    loading={isLoading}
                    loadingText={t('common:actions.loading')}
                    fullWidth
                    data-testid="register-submit-button"
                  >
                    {t('auth:register.submit')}
                  </FormButton>
                </form>
              </>
            )}

            {/* Login Section */}
            <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-700">
              <Text
                variant="bodySmall"
                className="text-center text-slate-600 dark:text-slate-400 mb-3"
              >
                {t('auth:register.hasAccount')}
              </Text>
              <FormButton
                type="button"
                variant="primary"
                onClick={() => {
                  const params = new URLSearchParams();
                  if (inviteToken) params.set('invite', inviteToken);
                  if (langParam) params.set('lang', langParam);
                  const query = params.toString();
                  router.push(query ? `/login?${query}` : '/login');
                }}
                fullWidth
                data-testid="register-login-link"
                style={{
                  backgroundImage: 'none',
                  backgroundColor: '#10b981',
                  borderColor: '#10b981',
                }}
              >
                {t('auth:register.signInLink')}
              </FormButton>
            </div>
          </Card>
        </div>
      </div>
      <Footer variant="minimal" />
    </div>
  );
}
